import { LeaguePlayer, PlayOutput } from '../generated-api';
import { comparePlayersByRank, conservativeRating } from './playerRanking';

/** "Active in" choices, in days. `null` means any time. */
export const ACTIVITY_WINDOWS = [
  { value: '', label: 'Any time', days: null },
  { value: '30', label: 'Last 30 days', days: 30 },
  { value: '90', label: 'Last 90 days', days: 90 },
  { value: '365', label: 'Last year', days: 365 },
] as const;

export interface LeaguePlayerFilters {
  /** Case-insensitive name search */
  search: string;
  /** Hide players with fewer than 5 games or sigma above 5 */
  excludeLowConfidence: boolean;
  /** Minimum games played in this league; 0 means no minimum */
  minGames: number;
  /** Only players with a play in the last N days; null means any time */
  activeWithinDays: number | null;
  /** Only players with a play of this game in the league; '' means any game */
  gameId: string;
}

export const DEFAULT_LEAGUE_PLAYER_FILTERS: LeaguePlayerFilters = {
  search: '',
  excludeLowConfidence: true,
  minGames: 0,
  activeWithinDays: null,
  gameId: '',
};

/** When each player last played in the league and which games they played. */
export type PlayerActivity = Map<string, { lastPlayedAt: number; gameIds: Set<string> }>;

export interface RankedLeaguePlayer extends LeaguePlayer {
  conservativeRating: number;
  isUncertain: boolean;
  /** 1-based rank among the players being shown; null for players with no games */
  rank: number | null;
}

/** True when any filter differs from its default (the low-confidence box counts when switched off). */
export const hasActiveLeaguePlayerFilters = (filters: LeaguePlayerFilters): boolean =>
  filters.search.trim() !== '' ||
  filters.excludeLowConfidence !== DEFAULT_LEAGUE_PLAYER_FILTERS.excludeLowConfidence ||
  filters.minGames > 0 ||
  filters.activeWithinDays !== null ||
  filters.gameId !== '';

/** True when the panel is narrowing the list (the low-confidence box is on by default, so it counts). */
export const isFilteringByPanel = (filters: LeaguePlayerFilters): boolean =>
  filters.excludeLowConfidence || filters.minGames > 0 || filters.activeWithinDays !== null || filters.gameId !== '';

/** True when any filter inside the filter panel differs from its default (search has its own button). */
export const hasActivePanelFilters = (filters: LeaguePlayerFilters): boolean =>
  hasActiveLeaguePlayerFilters({ ...filters, search: '' });

/** Per-player last play and played games, from every play in the league. */
export const buildPlayerActivity = (plays: PlayOutput[]): PlayerActivity => {
  const activity: PlayerActivity = new Map();
  for (const play of plays) {
    const playedAt = Date.parse(play.playDate);
    for (const playerId of Object.values(play.teams ?? {}).flat()) {
      let entry = activity.get(playerId);
      if (!entry) {
        entry = { lastPlayedAt: Number.NEGATIVE_INFINITY, gameIds: new Set() };
        activity.set(playerId, entry);
      }
      if (!Number.isNaN(playedAt) && playedAt > entry.lastPlayedAt) entry.lastPlayedAt = playedAt;
      entry.gameIds.add(play.gameId);
    }
  }
  return activity;
};

const isUncertain = (player: LeaguePlayer): boolean => player.gamesPlayed < 5 || player.sigma > 5;

/**
 * The players to show in the ranking list and the ratings chart, sorted and ranked.
 *
 * Players with games come first by conservative rating (rating - 3 sigma), then more games,
 * then name; players without games follow, by name. Ranks count only players with games,
 * within the filtered list.
 *
 * @param activity Needed for "Active in" and "Played game"; while null (plays still loading)
 *   those two filters are skipped rather than hiding everyone.
 */
export const filterLeaguePlayers = (
  players: LeaguePlayer[] | undefined,
  filters: LeaguePlayerFilters,
  activity: PlayerActivity | null,
  now: number = Date.now(),
): RankedLeaguePlayer[] => {
  const query = filters.search.trim().toLowerCase();
  const cutoff =
    filters.activeWithinDays !== null ? now - filters.activeWithinDays * 24 * 60 * 60 * 1000 : null;

  const kept = (players ?? [])
    .map(player => ({
      ...player,
      conservativeRating: conservativeRating(player.rating, player.sigma),
      isUncertain: isUncertain(player),
      rank: null as number | null,
    }))
    .filter(player => {
      if (query && !player.playerName.toLowerCase().includes(query)) return false;
      if (filters.excludeLowConfidence && player.isUncertain) return false;
      if (filters.minGames > 0 && player.gamesPlayed < filters.minGames) return false;
      if (activity && cutoff !== null) {
        const lastPlayedAt = activity.get(player.playerId)?.lastPlayedAt;
        if (lastPlayedAt === undefined || lastPlayedAt < cutoff) return false;
      }
      if (activity && filters.gameId) {
        if (!activity.get(player.playerId)?.gameIds.has(filters.gameId)) return false;
      }
      return true;
    })
    .sort((a, b) =>
      comparePlayersByRank(
        { name: a.playerName, rating: a.rating, sigma: a.sigma, gamesPlayed: a.gamesPlayed },
        { name: b.playerName, rating: b.rating, sigma: b.sigma, gamesPlayed: b.gamesPlayed },
      ),
    );

  let nextRank = 1;
  for (const player of kept) {
    if (player.gamesPlayed > 0) player.rank = nextRank++;
  }
  return kept;
};
